/**
 * @fileoverview Plain words for failures whose real cause is the connection,
 * shared by the code-runner start-up prompt, the error page and sign-in, so a
 * reader on a bad connection is told about the connection and not about the
 * part of the site that happened to fail first.
 */

import {useSyncExternalStore} from 'react';

/** What to tell a reader whose browser reports that it is offline. */
export const OFFLINE_MESSAGE =
  "You're offline. Reconnect to the internet, then try again.";

/**
 * Tells whether the browser reports that it has no network connection.
 *
 * `navigator.onLine` is only trustworthy when it is false: true means a
 * network interface is up, not that the site is reachable. So this can say a
 * failure was the connection, but its false result never rules that out.
 *
 * @param params.navigatorLike - Where to read `onLine` from. Omitted means the
 *     global `navigator`, or none outside a browser.
 * @return True only when the browser says it is offline.
 * @throws Never.
 */
export function isBrowserOffline({
  navigatorLike,
}: {
  navigatorLike?: Pick<Navigator, 'onLine'>;
} = {}): boolean {
  const source =
    navigatorLike ?? (typeof navigator === 'undefined' ? undefined : navigator);
  return source?.onLine === false;
}

function subscribeToConnectivity(onChange: () => void): () => void {
  window.addEventListener('online', onChange);
  window.addEventListener('offline', onChange);
  return () => {
    window.removeEventListener('online', onChange);
    window.removeEventListener('offline', onChange);
  };
}

function readIsOffline(): boolean {
  return isBrowserOffline();
}

/**
 * {@link isBrowserOffline}, kept current as the browser goes offline and back.
 *
 * @return True while the browser says it is offline.
 * @throws Never.
 */
export function useIsBrowserOffline(): boolean {
  return useSyncExternalStore(subscribeToConnectivity, readIsOffline);
}
